import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Building2, Linkedin, Twitter, Mail, Phone,
  Globe, FileText, ExternalLink, Clock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { PageLoader } from '@shared/components/PageLoader';
import { connectsAPI } from '@/utils/supabase/client';
import type { ContactDetailResponse, ConnectsMention, ConnectsRelationship } from '@/types/connects';
import { formatDistanceToNow, format } from 'date-fns';

function MentionRow({ mention }: { mention: ConnectsMention }) {
  const ki = mention.knox_items;
  return (
    <div className="py-3 border-b last:border-0">
      <div className="flex items-center gap-2 mb-1">
        <Badge variant="outline" className="text-xs">{ki?.source ?? 'unknown'}</Badge>
        {ki?.source_channel && <span className="text-xs text-muted-foreground">{ki.source_channel}</span>}
        {ki?.author_name && <span className="text-xs text-muted-foreground">· {ki.author_name}</span>}
        {ki?.occurred_at && (
          <span className="text-xs text-muted-foreground ml-auto">
            {formatDistanceToNow(new Date(ki.occurred_at), { addSuffix: true })}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground line-clamp-3">{mention.excerpt || ki?.content?.slice(0, 200)}</p>
    </div>
  );
}

function RelationshipRow({ rel, subjectId }: { rel: ConnectsRelationship; subjectId: string }) {
  const isFrom = rel.from_id === subjectId;
  const otherId = isFrom ? rel.to_id : rel.from_id;
  const otherType = isFrom ? rel.to_type : rel.from_type;
  const label = rel.kind.replace(/_/g, ' ');
  const confidence = Math.round(rel.confidence * 100);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 border-b last:border-0">
      <div className="flex items-center gap-2">
        {otherType === 'contact' ? <User className="h-4 w-4 text-muted-foreground" /> : <Building2 className="h-4 w-4 text-muted-foreground" />}
        <span className="text-xs text-muted-foreground">{otherType}</span>
      </div>
      <Badge variant="outline" className="text-xs">{label}</Badge>
      <span className="text-xs text-muted-foreground ml-auto">{confidence}% confidence</span>
      {rel.evidence?.[0]?.excerpt && (
        <p className="text-xs text-muted-foreground truncate w-full sm:w-auto sm:max-w-48">{rel.evidence[0].excerpt}</p>
      )}
    </div>
  );
}

export function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ContactDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    connectsAPI.getContact(id)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <PageLoader />;
  if (error || !data) return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
      <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">{error ?? 'Not found'}</div>
    </div>
  );

  const { contact, dossier, mentions, relationships } = data;

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6 overflow-auto">
      {/* Back + header */}
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/connects')} className="mb-3 -ml-2">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center shrink-0">
              <User className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold tracking-tight font-serif">{contact.display_name}</h1>
              {contact.title && <p className="text-sm text-muted-foreground">{contact.title}</p>}
              {contact.companies?.name && (
                <button
                  onClick={() => contact.company_id && navigate(`/connects/companies/${contact.company_id}`)}
                  className="text-sm text-primary hover:underline flex items-center gap-1 mt-0.5"
                >
                  <Building2 className="h-3 w-3" />
                  {contact.companies.name}
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 sm:ml-auto">
            {contact.tags.map((t) => <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: contact info + dossier */}
        <div className="space-y-4 lg:col-span-1">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm">Contact info</CardTitle></CardHeader>
            <CardContent className="pt-0 space-y-2">
              {contact.emails.map((e) => (
                <div key={e} className="flex items-center gap-2 text-sm">
                  <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                  <a href={`mailto:${e}`} className="hover:underline truncate">{e}</a>
                </div>
              ))}
              {contact.phones.map((p) => (
                <div key={p} className="flex items-center gap-2 text-sm">
                  <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{p}</span>
                </div>
              ))}
              {contact.linkedin_url && (
                <div className="flex items-center gap-2 text-sm">
                  <Linkedin className="h-3.5 w-3.5 text-muted-foreground" />
                  <a href={contact.linkedin_url} target="_blank" rel="noreferrer" className="hover:underline truncate flex items-center gap-1">
                    LinkedIn <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              {contact.twitter_handle && (
                <div className="flex items-center gap-2 text-sm">
                  <Twitter className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>@{contact.twitter_handle}</span>
                </div>
              )}
              {contact.location && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Globe className="h-3.5 w-3.5" />
                  {contact.location}
                </div>
              )}
              <Separator />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Mentions</span><span className="font-medium text-foreground">{contact.mention_count}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>First seen</span>
                <span>{format(new Date(contact.first_mentioned_at), 'MMM d, yyyy')}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Last seen</span>
                <span>{formatDistanceToNow(new Date(contact.last_mentioned_at), { addSuffix: true })}</span>
              </div>
            </CardContent>
          </Card>

          {/* Dossier */}
          {dossier && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Dossier
                  {data.dossier_refreshed_at && (
                    <span className="text-xs font-normal text-muted-foreground ml-auto">
                      <Clock className="h-3 w-3 inline mr-1" />
                      {formatDistanceToNow(new Date(data.dossier_refreshed_at), { addSuffix: true })}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-4">
                {dossier.linkedin?.headline && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">LinkedIn</p>
                    <p className="text-sm font-medium">{dossier.linkedin.headline}</p>
                    {dossier.linkedin.about && <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{dossier.linkedin.about}</p>}
                  </div>
                )}
                {dossier.press && dossier.press.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Recent press</p>
                    <div className="space-y-2">
                      {dossier.press.slice(0, 3).map((p, i) => (
                        <a key={i} href={p.url} target="_blank" rel="noreferrer" className="block hover:bg-accent rounded p-2 -m-2 group">
                          <p className="text-xs font-medium group-hover:underline line-clamp-2">{p.title}</p>
                          {p.publication && <p className="text-xs text-muted-foreground">{p.publication}</p>}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {dossier.site?.title && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Website</p>
                    <a href={dossier.site.url} target="_blank" rel="noreferrer" className="text-sm hover:underline flex items-center gap-1">
                      {dossier.site.title} <ExternalLink className="h-3 w-3" />
                    </a>
                    {dossier.site.meta_description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{dossier.site.meta_description}</p>}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: relationships + mention timeline */}
        <div className="space-y-4 lg:col-span-2">
          {relationships.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Relationships ({relationships.length})</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {relationships.map((r) => <RelationshipRow key={r.id} rel={r} subjectId={id!} />)}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Mention timeline ({mentions.length})</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {mentions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No mentions recorded yet</p>
              ) : (
                mentions.map((m, i) => <MentionRow key={i} mention={m} />)
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
